import * as React from "react";
import { Icon } from "@utdanningsdirektoratet/icon";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { IconButton } from "@utdanningsdirektoratet/button";
import { parseDate } from "@utdanningsdirektoratet/utils/date";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import ReactDatepicker, {
  ReactDatePickerCustomHeaderProps,
  registerLocale,
  setDefaultLocale,
} from "react-datepicker";
import { nb } from "date-fns/locale";
import { format } from "date-fns";
import styles from "./DatePicker.module.less";

registerLocale("nb", nb);
setDefaultLocale("nb");

const DatepickerHeader = (props: ReactDatePickerCustomHeaderProps) => {
  return (
    <div className={styles.datePickerHeader}>
      <IconButton
        disabled={props.prevMonthButtonDisabled}
        aria-label="Forrige måned"
        onClick={() => props.decreaseMonth()}
      >
        <Icon icon="angle left" />
      </IconButton>
      <div className={styles.datePickerHeaderText}>
        {props.date.toLocaleString("nb", { month: "long" })}{" "}
        {props.date.getFullYear()}
      </div>
      <IconButton
        disabled={props.nextMonthButtonDisabled}
        aria-label="Neste måned"
        onClick={() => props.increaseMonth()}
      >
        <Icon icon="angle right" />
      </IconButton>
    </div>
  );
};

const getUTCDate = (
  date: Date | [Date | null, Date | null] | Date[] | null | undefined,
) =>
  date instanceof Date
    ? new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0),
      )
    : undefined;

interface DatePickerProps
  extends Omit<
    React.ComponentProps<typeof ReactDatepicker>,
    | "value"
    | "onChange"
    | "onBlur"
    | "selectsMultiple"
    | "selectsRange"
    | "showMonthYearDropdown"
  > {
  id?: string;
  label?: React.ReactNode | string;
  value?: Date | undefined;
  message?: React.ReactNode | string;
  error?: boolean;
  align?: "left" | "right";
  className?: string;
  placeholder?: string;
  dateFormat?: string;
  disabled?: boolean;
  onChange: (
    date: Date | undefined,
    event: React.SyntheticEvent | undefined,
  ) => void;
  onBlur?: (date: Date | undefined) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({
  disabled,
  placeholder,
  dateFormat = "dd.MM.yyyy",
  message,
  label,
  value,
  align = "left",
  error,
  className,
  onChange,
  onBlur,
  ...rest
}) => {
  const id = useId(rest.id);
  const [isFocused, setFocused] = React.useState(false);

  const datepickerClass = cls({
    [styles.datePicker]: true,
    [styles.datePickerError]: error,
    [styles.datePickerDisabled]: disabled,
    [styles.datePickerEmpty]: false,
    [styles.datePickerWithIcon]: error,
  });

  const iconClass = cls({
    [styles.datePickerIcon]: true,
    [styles.datePickerIconVisible]: error,
  });

  const labelClass = cls({
    [styles.datePickerLabel]: true,
    [styles.datePickerLabelFocused]: isFocused,
  });

  const wrapperClass = cls(
    {
      [styles.datePickerWrapper]: true,
    },
    className,
  );

  const onCalendarOpen = (): void => {
    setFocused(true);
  };

  const onBlurWrapped = (): void => {
    if (onBlur) {
      onBlur(getUTCDate(value));
    }
    setFocused(false);
  };

  const onCalendarClose = (): void => {
    onBlurWrapped();
  };

  return (
    <div className={wrapperClass}>
      <label htmlFor={id} id={`${id}-label`} className={labelClass}>
        {label}
        <Tooltip
          message={message}
          disabled={!error}
          alwaysShow={error}
          align="right"
          alert
        >
          {({ ref: tooltipRef, ...tooltipProps }) => (
            <div ref={tooltipRef} className={styles.datePickerTooltipContent}>
              <Icon icon="calendar" className={styles.datePickerCalendericon} />
              <ReactDatepicker
                {...tooltipProps}
                autoComplete="off"
                selected={value ? parseDate(value) : value}
                onChange={(date, event) => onChange(getUTCDate(date), event)}
                placeholderText={placeholder}
                locale="nb"
                id={id}
                popperPlacement={
                  align === "left" ? "bottom-start" : "bottom-end"
                }
                className={datepickerClass}
                disabled={disabled}
                ariaInvalid={error ? "true" : undefined}
                ariaDescribedBy={tooltipProps["aria-describedby"]}
                renderCustomHeader={DatepickerHeader}
                portalId={`${id}-portal`}
                onCalendarOpen={onCalendarOpen}
                onCalendarClose={onCalendarClose}
                dateFormat={dateFormat}
                chooseDayAriaLabelPrefix="Velg"
                weekAriaLabelPrefix="Uke"
                nextYeararia-label="Neste år"
                nextMontharia-label="Neste måned"
                disabledDayAriaLabelPrefix="Utilgjengelig"
                previousYeararia-label="Forrige år"
                previousMontharia-label="Forrige måned"
                ariaLabelClose="Lukk"
                formatWeekDay={(date: string) => {
                  let nameOfDay: string;
                  if (typeof date !== "string") {
                    nameOfDay = format(date, "EEEE", { locale: nb });
                  } else {
                    nameOfDay = date;
                  }
                  return nameOfDay[0].toUpperCase() + nameOfDay[1];
                }}
                showPopperArrow={false}
                shouldCloseOnSelect
                onBlur={onBlurWrapped}
                {...rest}
              />
              <Icon
                className={iconClass}
                color="darkred"
                aria-hidden={error ? "false" : "true"}
                icon="warning"
              />
            </div>
          )}
        </Tooltip>
      </label>
      <div id={`${id}-portal`} className={styles.datePickerReactDatepicker} />
    </div>
  );
};

export default DatePicker;
